import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SEO from '../components/SEO';
import axios from 'axios';
import { API, cacheBuster } from '../config/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { 
  ArrowRight, Check, Loader2, Phone, MapPin, Shield, Clock, Zap, Star
} from 'lucide-react';
import { toast } from 'sonner';

const services = [
  { id: 'decorating', name: 'Decorating / Painting' },
  { id: 'carpentry', name: 'Carpentry / Doors' },
  { id: 'outdoors', name: 'Outdoors & Garden' },
  { id: 'electrics', name: 'Electrics' },
  { id: 'plastering', name: 'Plastering & Tiling' },
  { id: 'building', name: 'Building & Maintenance' },
  { id: 'general', name: 'General Tasks' },
  { id: 'cleaning', name: 'Cleaning Services' },
  { id: 'landlord', name: 'Landlord Services' },
  { id: 'loft', name: 'Loft Services' },
  { id: 'multiple', name: 'Multiple Jobs' }
];

export const Quote = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    service: '',
    description: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.phone || !formData.description) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    
    try {
      await axios.post(`${API}/contact?${cacheBuster()}`, {
        name: formData.name,
        email: formData.email || '',
        phone: formData.phone,
        address: '',
        job_type: formData.service || 'General Enquiry',
        job_description: formData.description
      });
      
      setSubmitted(true);
      toast.success('Thanks! We\'ll be in touch soon.');
    } catch (error) {
      console.error('Failed to submit:', error);
      toast.error('Something went wrong. Please call us instead.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-background to-muted/30 py-12 px-4">
        <SEO 
          title="Thanks for Your Enquiry | Expert-Handyman"
          description="We've received your enquiry and will be in touch soon."
        />
        <div className="max-w-lg mx-auto text-center">
          <div className="bg-green-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
            <Check className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="text-3xl font-bold font-['Outfit'] mb-4">Thanks for Getting in Touch!</h1>
          <p className="text-muted-foreground mb-6">
            We&apos;ve received your enquiry and will call you back shortly to discuss your project.
          </p>
          <div className="bg-white rounded-xl p-6 shadow-sm mb-6">
            <p className="font-medium mb-2">Need it sooner?</p>
            <a 
              href="tel:07300405045" 
              className="text-2xl font-bold text-primary hover:underline"
            >
              07300 405045
            </a>
          </div>
          <Button onClick={() => navigate('/')} variant="outline">
            Back to Home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/30">
      <SEO 
        title="Get a Free Quote | Door Fitting, Painting, Handyman Near Me"
        description="Get a free handyman quote in Manchester & Cheshire. Door fitting, skirting boards, architraves, wall panelling, painting, decorating, plumbing, electrics & plastering. No VAT. Call 07300 405045."
        keywords="free handyman quote near me, handyman quote Manchester, door fitting quote, painting quote near me, decorating quote near me, plumber quote Manchester, get a quote handyman"
        canonical="/quote"
      />
      
      {/* Trust Bar */}
      <div className="bg-primary text-white py-2">
        <div className="max-w-7xl mx-auto px-4 flex flex-wrap justify-center gap-6 text-sm">
          <span className="flex items-center gap-2">
            <Shield className="w-4 h-4" /> 12-Month Guarantee
          </span>
          <span className="flex items-center gap-2">
            <Zap className="w-4 h-4" /> Price Promise
          </span>
          <span className="flex items-center gap-2">
            <Check className="w-4 h-4" /> No VAT
          </span>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-12">
        <div className="grid lg:grid-cols-2 gap-12">
          
          {/* Left Column - Form */}
          <div>
            <p className="text-muted-foreground mb-2">Get In Touch</p>
            <h1 className="text-3xl md:text-4xl font-bold font-['Outfit'] mb-4">
              Get a Free Quote
            </h1>
            <p className="text-muted-foreground mb-6">
              Tell us about your job and we&apos;ll get back to you with a quote. Simple jobs can often be quoted over the phone or from photos.
            </p>

            {/* Phone CTA */}
            <a 
              href="tel:07300405045"
              className="inline-flex items-center gap-3 bg-primary text-white px-6 py-3 rounded-lg font-semibold mb-8 hover:bg-primary/90 transition-colors"
            >
              <Phone className="w-5 h-5" />
              Call 07300 405045
            </a>

            {/* Form */}
            <Card>
              <CardContent className="p-6">
                <h3 className="font-semibold text-lg mb-4">Tell Us About Your Job</h3>
                
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="name">Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      data-testid="name-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="phone">Phone *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      data-testid="phone-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      data-testid="email-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="service">What do you need? *</Label>
                    <select
                      id="service"
                      name="service"
                      value={formData.service}
                      onChange={handleChange}
                      required
                      className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                      data-testid="service-select"
                    >
                      <option value="">Select a service</option>
                      {services.map(s => (
                        <option key={s.id} value={s.name}>{s.name}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <Label htmlFor="description">Job Details *</Label>
                    <Textarea
                      id="description"
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      placeholder="Tell us what you need done..."
                      rows={4}
                      required
                      data-testid="description-input"
                    />
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full btn-accent py-6 text-lg"
                    disabled={loading}
                    data-testid="submit-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin mr-2" />
                        Sending...
                      </>
                    ) : (
                      <>
                        Send Enquiry
                        <ArrowRight className="w-5 h-5 ml-2" />
                      </>
                    )}
                  </Button>

                  {/* Form Trust Signals */}
                  <div className="flex justify-center gap-6 text-sm text-muted-foreground pt-2">
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" /> Quick response
                    </span>
                    <span className="flex items-center gap-1">
                      <Check className="w-4 h-4" /> No spam, ever
                    </span>
                    <span className="flex items-center gap-1">
                      <Check className="w-4 h-4" /> Free quote
                    </span>
                  </div>
                </form>
              </CardContent>
            </Card>

            {/* Testimonial */}
            <div className="mt-6 bg-muted/50 rounded-xl p-5">
              <div className="flex gap-1 mb-2">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                ))}
              </div>
              <p className="text-muted-foreground italic mb-2">
                &quot;Quick response to my enquiry, fair quote, and did a great job. Would definitely recommend.&quot;
              </p>
              <p className="text-sm font-medium">— Sarah M., Stockport</p>
            </div>
          </div>

          {/* Right Column - Contact Details */}
          <div className="lg:pl-8">
            <h2 className="text-2xl font-bold font-['Outfit'] mb-6">Contact Details</h2>
            
            {/* Phone Card */}
            <a 
              href="tel:07300405045"
              className="block bg-white border border-border rounded-xl p-6 mb-6 hover:shadow-lg transition-shadow"
            >
              <p className="text-muted-foreground text-sm mb-1">Call or text</p>
              <p className="text-2xl font-bold text-primary">07300 405045</p>
            </a>

            {/* Location */}
            <div className="mb-8">
              <p className="text-muted-foreground text-sm mb-1">Based in</p>
              <p className="font-semibold flex items-center gap-2">
                <MapPin className="w-4 h-4 text-primary" />
                Greater Manchester
              </p>
            </div>

            {/* Areas Covered */}
            <div className="mb-8">
              <h3 className="font-semibold mb-3">Areas Covered</h3>
              <p className="text-muted-foreground">
                Greater Manchester, Stockport, Cheadle, Bramhall, Poynton, Macclesfield, Wilmslow, Marple, Cheshire, and surrounding areas.
              </p>
            </div>

            {/* What You Get */}
            <div className="bg-muted/50 rounded-xl p-6">
              <h3 className="font-semibold mb-4">What You Get</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span>12-month guarantee on all work</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span>We&apos;ll beat any like-for-like quote by 10%</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span>No VAT - price quoted is what you pay</span>
                </li>
              </ul>
            </div>

            {/* CTA Buttons */}
            <div className="flex gap-4 mt-8">
              <a href="tel:07300405045" className="flex-1">
                <Button className="w-full" size="lg">
                  <Phone className="w-4 h-4 mr-2" />
                  Call Now
                </Button>
              </a>
              <Button 
                variant="outline" 
                size="lg" 
                className="flex-1"
                onClick={() => document.getElementById('name')?.focus()}
              >
                Get Quote
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Quote;
